/* =========================================================================
 * Bripplex — Homepage (2026 redesign).
 *
 * Positioning shift (per brief): community-powered environmental intelligence,
 * not a crypto application. Sections (top to bottom):
 *   1. Hero
 *   2. How It Works (4 steps; Get Rewarded last on purpose)
 *   3. Global Network — placeholder for interactive map (next iteration)
 *   4. Contribution & Achievements — placeholder
 *   5. Research Impact — placeholder
 *   6. Trust & Credibility — placeholder
 *
 * Pre-existing Creative-Tim demo sections (Counters, DesignBlocks, etc.) were
 * removed wholesale — they were template content with no Bripplex copy.
 * ========================================================================= */

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

// Routes
import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

// Sections
import { HowItWorks } from "pages/presentation/components/HowItWorks";
import { GlobalNetwork } from "pages/presentation/components/GlobalNetwork";
import { Achievements } from "pages/presentation/components/Achievements";
import { ResearchImpact } from "pages/presentation/components/ResearchImpact";
import { Trust } from "pages/presentation/components/Trust";
import { Download } from "pages/presentation/components/Download";

// Assets
import brippleIcon from "assets/images/logos/bripplex/icon.svg";

function Presentation() {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      <HeroSection />

      <MKBox
        component="section"
        sx={{
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          borderRadius: 3,
          backgroundColor: ({ palette: { white } }) => white.main,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          overflow: "hidden",
        }}
      >
        <HowItWorks />
        <GlobalNetwork />
        <Achievements />
        <ResearchImpact />
        <Trust />
        <Download />
      </MKBox>

      <SiteFooter content={footerRoutes} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function HeroSection() {
  return (
    <MKBox
      minHeight="78vh"
      width="100%"
      sx={({ palette }) => ({
        background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 60%, #DBEAFE 100%)`,
        display: "grid",
        placeItems: "center",
        position: "relative",
        pt: { xs: 12, md: 16 },
        pb: { xs: 10, md: 14 },
      })}
    >
      <Container>
        <Grid
          container
          item
          xs={12}
          md={10}
          lg={9}
          justifyContent="center"
          alignItems="center"
          mx="auto"
          textAlign="center"
        >
          <MKBox
            component="img"
            src={brippleIcon}
            alt=""
            sx={{ height: { xs: 56, md: 72 }, width: "auto", mb: 3 }}
          />
          <MKTypography
            variant="h1"
            color="dark"
            sx={({ breakpoints, typography: { size } }) => ({
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              [breakpoints.down("md")]: { fontSize: size["3xl"] },
            })}
          >
            Transform everyday devices into{" "}
            <MKBox component="span" sx={{ color: "primary.main" }}>
              environmental intelligence
            </MKBox>
          </MKTypography>
          <MKTypography
            variant="body1"
            color="text"
            mt={3}
            sx={{ maxWidth: 720, fontSize: { xs: 16, md: 18 }, lineHeight: 1.55 }}
          >
            Join a global network helping researchers, universities, and environmental organizations
            better understand the world through real-time community data.
          </MKTypography>
          <MKBox mt={5} display="flex" gap={2} flexWrap="wrap" justifyContent="center">
            <MKButton
              variant="gradient"
              color="primary"
              size="large"
              href="#download"
              sx={{ px: 4 }}
            >
              Download App
            </MKButton>
            <MKButton
              variant="outlined"
              color="dark"
              size="large"
              href="/researchers"
              sx={{ px: 4 }}
            >
              For Researchers
            </MKButton>
          </MKBox>
        </Grid>
      </Container>
    </MKBox>
  );
}

// Sub-sections are extracted to their own files under
// `pages/presentation/components/*`. The Presentation component above just
// composes them in the order the brief specifies.

export default Presentation;
